import { Button } from '@mui/material';

import { cn } from '@/shared/lib/cn';

import { ACTION_SHAPE, appearanceOfNeed, type ActionAppearance } from './actionAppearance';
import { actionIconOf } from './actionIcon';
import type { OutstandingAction } from './rowActions';

interface ActionButtonProps {
  action: OutstandingAction;
  onSelect: (action: OutstandingAction) => void;
  fullWidth?: boolean;
}

/**
 * A row's primary action, styled by what it does.
 *
 * Pay and Sign are deliberately NOT the same button. They commit a director to
 * different things — one moves money and is irreversible, the other attaches
 * their signature — and the legacy portal rendered both as the same blue
 * `color="primary"` control, so the two were told apart only by reading the
 * label. On a row that needs both, that is the difference between paying a fee
 * and signing a resolution.
 *
 * The pay/sign treatment itself lives in `actionAppearance`, shared with the
 * card button that opens the guided flow, so the two surfaces cannot drift
 * apart again. The icon comes from the same map the overflow menu reads — the
 * button and the menu item for one action must never wear different icons.
 */
export const ActionButton = ({ action, onSelect, fullWidth = false }: ActionButtonProps) => {
  const shared = {
    size: 'small' as const,
    disabled: action.disabled === true,
  };

  // Width is a class rather than MUI's `fullWidth` so the button can give the
  // overflow trigger its space: `flex-1` claims what is left of the phone row
  // instead of the whole of it, and releases it once there is room to sit right.
  const shape = cn(ACTION_SHAPE, 'px-3.5', fullWidth && 'flex-1 sm:flex-none');

  // A link action neither pays nor signs — it fetches a file — so it takes the
  // quiet primary text button rather than either obligation's tone.
  const appearance: ActionAppearance =
    action.kind === 'pay' || action.kind === 'sign'
      ? appearanceOfNeed(action.kind)
      : { variant: 'text', color: 'primary', className: '' };

  const icon = actionIconOf(action.kind);

  // Every action — including the ones that reach a file — now goes through
  // `onSelect`. A link action used to be a real anchor, which was right while
  // following it meant leaving the portal; it opens a reader over the screen
  // instead, and an anchor would navigate away before that reader could mount.
  return (
    <Button
      {...shared}
      variant={appearance.variant}
      color={appearance.color}
      className={cn(shape, appearance.className)}
      startIcon={icon}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(action);
      }}
    >
      {action.label}
    </Button>
  );
};
